import { isPast } from "date-fns"
import _ from "lodash"
import pluralize from "pluralize"
import {
  DioboxEvent, Form, FormStatus, FormType, Email, Subscription,
  SubscriptionType,
} from "../sharedTypes"
import { asMoney } from "./strings"

export const setValueIfNotEqual = (setValue, fieldName, previousValue, nextValue) => {
  if (previousValue !== nextValue) {
    setValue(fieldName, nextValue)
  }
}

export const formIconAndType = (formType: FormType): {
  icon: string, type: string
} => {
  const data = {
    icon: "", type: "", status: FormStatus.Published, color: "status.published",
  }

  switch (formType) {
    case FormType.Registration: {
      data.icon = "form-registration"
      data.type = "Registration"
      break
    }
    case FormType.Ticket: {
      data.icon = "form-ticket"
      data.type = "Ticket"
      break
    }
    case FormType.RSVP: {
      data.icon = "form-rsvp"
      data.type = "RSVP"
      break
    }
    case FormType.Waitlist: {
      data.icon = "form-waitlist"
      data.type = "Waitlist"
      break
    }
    case FormType.Survey: {
      data.icon = "form-survey"
      data.type = "Survey"
      break
    }
    default:
  }

  return data
}

export const submissionIconAndType = (lineItems: { form: { type: FormType } }[]) => {
  if (lineItems.length <= 1) {
    return formIconAndType(lineItems[0]?.form.type)
  }

  const counts = Object.entries(_.countBy(lineItems, "form.type"))

  return {
    icon: "multi-registration",
    type: counts.map(([type, count]) => `${count} ${pluralize(type, count)}`).join(", "),
  }
}

export const formStatusLabel = (status: FormStatus, type: FormType): string => {
  switch (status) {
    case FormStatus.SoldOut: {
      switch (type) {
        case FormType.Waitlist:
          return "At Capacity"
        case FormType.Registration:
          return "At Capacity"
        default:
          return "Sold Out"
      }
    }
    case FormStatus.Ended:
      return "Ended"
    case FormStatus.EventFull:
      return "At Capacity"
    case FormStatus.Disabled:
      return "Disabled"
    case FormStatus.GatewayNotConnected:
      return "Gateway Not Connected"
    default:
      return "Published"
  }
}

export const formStatusAndColor = (
  form: Form,
  event: DioboxEvent,
): {status: FormStatus, color: string} => {
  const {
    disabled, submissionsEndAt, submissionLineItemsCount, quantity,
  } = form
  const { stripeAccountId } = event

  if (quantity && quantity <= submissionLineItemsCount) {
    return {
      status: FormStatus.SoldOut,
      color: "status.inactive",
    }
  }

  if (disabled) {
    return {
      status: FormStatus.Disabled,
      color: "status.disabled",
    }
  }

  if (submissionsEndAt && isPast(new Date(submissionsEndAt))) {
    return {
      status: FormStatus.Ended,
      color: "status.ended",
    }
  }

  if (!stripeAccountId && form.type === FormType.Ticket) {
    return {
      status: FormStatus.GatewayNotConnected,
      color: "status.inactive",
    }
  }

  if (form.spotsLeft === 0) {
    return {
      status: FormStatus.EventFull,
      color: "status.inactive",
    }
  }

  return {
    status: FormStatus.Published,
    color: "status.published",
  }
}

export const isListedOnPortal = (form: Form, event: DioboxEvent) => {
  if (form.private || form.disabled) {
    return false
  }

  if (form.type === FormType.Ticket) {
    return Boolean(event.stripeAccountId)
  }

  return [FormType.Registration, FormType.Waitlist].includes(form.type)
}

export const getEnabledFormTypesBySubscriptionLevel = (subscription: Subscription | null) => {
  const privateRSVPEnabled = subscription && [
    SubscriptionType.Professional,
    SubscriptionType.Corporate,
    SubscriptionType.Enterprise,
  ].includes(subscription.type)
  const waitlistEnabled = subscription && [
    SubscriptionType.Corporate,
    SubscriptionType.Enterprise,
  ].includes(subscription.type)
  const surveyEnabled = subscription && [
    SubscriptionType.Corporate,
    SubscriptionType.Enterprise,
  ].includes(subscription.type)

  return [
    FormType.Registration,
    FormType.Ticket,
    ...(privateRSVPEnabled ? [FormType.RSVP] : []),
    ...(waitlistEnabled ? [FormType.Waitlist] : []),
    ...(surveyEnabled ? [FormType.Survey] : []),
  ]
}

export const submissionIsPaid = (price: string | null) => price && parseFloat(price) > 0

export const getFormsByType = (forms: Form[], type: FormType) => forms.filter(
  (form: Form) => form.type === type,
)

export const getFormsByTypeWithVisibilityAndStatus = (
  forms: Form[],
  formType: FormType,
  event: DioboxEvent,
) => getFormsByType(
  forms, formType,
).map((form) => {
  const { name, externalId, type } = form
  const { status } = formStatusAndColor(form, event)

  const label = `${name}${status !== FormStatus.Published ? ` (${formStatusLabel(status, type)})` : ""}`

  return { label, value: externalId, disabled: status === FormStatus.Disabled }
})

export const emailIcon = (email: Email) => (email.hasForm ? "email-with-form" : "email-plain")

export const requiresUpgrade = (integratedForms: Form[], subscriptionType: Subscription | null) => {
  const enabledFormTypes = getEnabledFormTypesBySubscriptionLevel(subscriptionType)

  return integratedForms.some(({ type }) => !enabledFormTypes.includes(type))
}

export const getFormsGroupedByType = (forms: Form[], event: DioboxEvent) => [
  {
    label: "Registration Forms",
    options: getFormsByTypeWithVisibilityAndStatus(forms, FormType.Registration, event),
  },
  {
    label: "Ticket Forms",
    options: getFormsByTypeWithVisibilityAndStatus(forms, FormType.Ticket, event),
  },
  {
    label: "RSVP Forms",
    options: getFormsByTypeWithVisibilityAndStatus(forms, FormType.RSVP, event),
  },
  {
    label: "Waitlist Forms",
    options: getFormsByTypeWithVisibilityAndStatus(forms, FormType.Waitlist, event),
  },
  {
    label: "Survey Forms",
    options: getFormsByTypeWithVisibilityAndStatus(forms, FormType.Survey, event),
  },
].filter((group) => Boolean(group.options.length))

export const templateSubmitButtonLabel = (form: Form) => {
  const {
    type,
    translations:
    {
      rsvpSubmitButton,
      registrationSubmitButton,
      ticketSubmitButton,
      waitlistSubmitButton,
      surveySubmitButton,
    },
  } = form

  switch (type) {
    case FormType.RSVP:
      return rsvpSubmitButton
    case FormType.Registration:
      return registrationSubmitButton
    case FormType.Ticket:
      return ticketSubmitButton
    case FormType.Waitlist:
      return waitlistSubmitButton
    case FormType.Survey:
      return surveySubmitButton
    default:
      return ""
  }
}

export const submitButtonPriceSuffix = (form: Form) => (
  form.variablePrice ? "" : asMoney(form.price, form.currency)
)

export const generateSubmitButtonText = (form: Form) => {
  const label = form.submitButtonLabel?.trim() || templateSubmitButtonLabel(form)

  return form.type === FormType.Ticket ? `${label} ${submitButtonPriceSuffix(form)}` : label
}

export const isGuestStrategyEnabled = (type: FormType) => [
  FormType.Registration,
  FormType.Ticket,
  FormType.Waitlist,
].includes(type)

export const capacityAppliesTo = (type: FormType) => [
  FormType.Registration,
  FormType.Ticket,
  FormType.RSVP,
].includes(type)
